import * as React from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { Label } from "@/v2/ui/primitives/label";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => {
    return (
      <textarea
        className={cn(
          "tw-flex tw-min-h-[80px] tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-ring-offset-background placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export interface FormTextareaProps {
  name: string;
  label?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  rows?: number;
  className?: string;
  maxLength?: number;
}

export const FormTextarea: React.FC<FormTextareaProps> = ({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  rows = 4,
  className,
  maxLength,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = get(errors, name);
  const errorMessage = error?.message as string | undefined;

  return (
    <div
      className={cn("tw-space-y-1", className)}
      data-testid={TEST_IDS.formField(name)}
    >
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <span className="tw-text-destructive tw-ml-1">*</span>}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{
          required: required
            ? strings("validation.fieldRequired", {
                field:
                  typeof label === "string" ? label : strings("form.thisField"),
              })
            : false,
        }}
        render={({ field }) => (
          <>
            <Textarea
              {...field}
              id={name}
              placeholder={placeholder}
              disabled={disabled}
              rows={rows}
              maxLength={maxLength}
              className={cn(
                error &&
                  "tw-border-destructive focus-visible:tw-ring-destructive"
              )}
            />
            {maxLength && (
              <p className="tw-text-xs tw-text-muted-foreground tw-text-right tw-mt-1">
                {field.value?.length || 0}/{maxLength}
              </p>
            )}
          </>
        )}
      />
      {errorMessage && (
        <p
          className="tw-text-sm tw-text-destructive tw-mt-1"
          data-testid={TEST_IDS.formError(name)}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
